if (!customElements.get('cart-item-size')) {
  customElements.define(
    'cart-item-size',
    class CartItemSize extends HTMLElement {
      constructor() {
        super();
        this.toggle = this.querySelector('.cart-item-size__toggle');
        this.panel = this.querySelector('.cart-item-size__panel');
        if (!this.panel) return; // Only one size offered — nothing to open.

        this.toggle.addEventListener('click', this.onToggleClick.bind(this));
        this.panel.addEventListener('click', this.onPanelClick.bind(this));
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
      }

      get isOpen() {
        return !this.panel.hidden;
      }

      onToggleClick(event) {
        event.preventDefault();
        this.isOpen ? this.close() : this.open();
      }

      open() {
        document.querySelectorAll('cart-item-size').forEach((other) => {
          if (other !== this) other.close();
        });

        this.panel.hidden = false;
        this.toggle.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keyup', this.onKeyUp);
      }

      close() {
        if (!this.isOpen) return;
        this.panel.hidden = true;
        this.toggle.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keyup', this.onKeyUp);
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) this.close();
      }

      onKeyUp(event) {
        if (event.code?.toUpperCase() !== 'ESCAPE') return;
        this.close();
        this.toggle.focus();
      }

      onPanelClick(event) {
        const option = event.target.closest('.cart-item-size__option');
        if (!option || option.disabled || option.classList.contains('cart-item-size__option--current')) return;
        this.close();
        // Reflects the pick immediately — the add/remove round-trip below
        // (and the DOM patch it ends with) still runs and is what actually
        // persists the change, but this way the shopper never sees a lag
        // that reads as nothing having happened.
        this.toggle.firstChild.textContent = option.textContent.trim();
        this.changeSize(option.dataset.variantId);
      }

      // A cart line's variant can't be swapped in place via the Ajax Cart
      // API — /cart/change.js only ever adjusts an existing line's quantity/
      // properties, never which variant it points at. So this adds the new
      // size as its own line first (carrying over quantity/properties/
      // selling plan), then removes the original line by its position, same
      // two-step approach product pages use for "swap variant" quick-adds.
      //
      // A previous version of this tried to hand-patch just this one row
      // afterward instead of letting the shared CartItems.onCartUpdate
      // (assets/cart.js) do its usual full re-render of the whole list —
      // matching which freshly-rendered line was actually the swapped one
      // turned out to have edge cases that picked the wrong line, showing
      // the OLD size again after a real update had already gone through.
      // Publishing cartUpdate with a normal source lets that shared,
      // already-proven refresh handle it instead — always reflects the
      // real server state, at the cost of re-rendering the whole list
      // (closing any other row's own open dropdown) rather than just this
      // one row.
      changeSize(newVariantId) {
        if (!newVariantId || this.isChanging) return;
        this.isChanging = true;
        this.classList.add('cart-item-size--loading');

        const lineKey = this.dataset.lineKey;
        const quantity = parseInt(this.dataset.quantity, 10) || 1;
        const sellingPlanId = this.dataset.sellingPlanId;

        let properties = null;
        try {
          properties = JSON.parse(this.dataset.properties || 'null');
        } catch (error) {
          properties = null;
        }

        const addItem = { id: newVariantId, quantity };
        if (properties && Object.keys(properties).length) addItem.properties = properties;
        if (sellingPlanId) addItem.selling_plan = sellingPlanId;

        fetch(`${routes.cart_add_url}`, {
          ...fetchConfig(),
          body: JSON.stringify({ items: [addItem] }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'cart-item-size',
                productVariantId: newVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              return null;
            }

            return fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({ id: lineKey, quantity: 0 }),
            }).then((changeResponse) => changeResponse.json());
          })
          .then((cartState) => {
            if (!cartState) return;
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-item-size',
              productVariantId: newVariantId,
              cartData: cartState,
            });
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.isChanging = false;
            this.classList.remove('cart-item-size--loading');
          });
      }
    }
  );
}
